import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heartbeat, Lightbulb, ChatsCircle, Microphone, ClipboardText, Wrench, Lifebuoy, Robot, ChatText, Sparkle } from "@phosphor-icons/react";
import { OutcomeChip } from "@/components/admin/OutcomeChip";
import { CcPanel, Empty } from "./CcPanel";
import { C, timeAgo } from "./ccUtils";

const STATUS = { ok: C.green, warn: C.amber, off: C.muted };

export function HealthPanel({ health }) {
  return (
    <CcPanel title="Platform health" icon={Heartbeat} testid="panel-platform-health" className="h-[212px]" delay={0.05}>
      <div className="divide-y divide-neutral-800/70">
        {health.map((h) => (
          <div key={h.key} data-testid={`health-${h.key}`} data-status={h.status} className="flex items-center gap-2.5 py-[5px]">
            <span className={`w-2 h-2 rounded-full shrink-0 ${h.status === "ok" ? "pulse-dot" : ""}`} style={{ background: STATUS[h.status] }} />
            <span className="text-xs text-neutral-200 truncate">{h.label}</span>
            <span className="ml-auto text-[11px] shrink-0 text-right" style={{ color: h.status === "off" ? "#737373" : STATUS[h.status] }}>
              {h.detail}{h.at && <span className="text-neutral-600 font-mono"> · {timeAgo(h.at)}</span>}
            </span>
          </div>
        ))}
      </div>
    </CcPanel>
  );
}

function insightIcon(text) {
  const t = text.toLowerCase();
  if (t.startsWith("ai ")) return Robot;
  if (t.includes("fault")) return Wrench;
  if (t.includes("ticket")) return Lifebuoy;
  if (t.includes("topic")) return ChatText;
  if (t.includes("nick")) return ChatsCircle;
  if (t.includes("warranty")) return ClipboardText;
  return Sparkle;
}

export function InsightsPanel({ insights }) {
  return (
    <CcPanel title="Executive insights" icon={Lightbulb} testid="panel-executive-insights" className="flex-1" delay={0.1}>
      <ul className="space-y-2">
        {insights.map((text, i) => {
          const Icon = insightIcon(text);
          return (
            <motion.li key={text} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}
              data-testid={`insight-${i}`} className="flex gap-2.5 items-start">
              <span className="w-5 h-5 rounded-md bg-[#73bf44]/10 border border-[#73bf44]/20 flex items-center justify-center shrink-0 mt-px">
                <Icon size={12} weight="duotone" className="text-[#73bf44]" />
              </span>
              <span className="text-[12.5px] leading-snug text-neutral-200">{text}</span>
            </motion.li>
          );
        })}
      </ul>
    </CcPanel>
  );
}

function RecentRow({ c, interactive }) {
  const navigate = useNavigate();
  const Tag = interactive ? "button" : "div";
  return (
    <Tag data-testid={`recent-conversation-${c.id}`} onClick={interactive ? () => navigate(`/admin/conversations/${c.id}`) : undefined}
      className={`w-full flex items-center gap-2 text-left rounded-lg px-2 py-1 ${interactive ? "hover:bg-white/[0.04] transition-colors" : ""}`}>
      <div className="min-w-0 flex-1">
        <div className="text-xs text-neutral-200 truncate">{c.last_message || "New conversation"}</div>
        <div className="text-[10px] text-neutral-500 truncate">{c.chassis_number ? `${c.model || "Caravan"} · ${c.chassis_number}` : "No caravan linked"} · {timeAgo(c.updated_at)}</div>
      </div>
      {c.has_voice && <Microphone size={12} weight="fill" className="text-sky-300 shrink-0" />}
      <OutcomeChip outcome={c.outcome} />
    </Tag>
  );
}

export function RecentPanel({ recent, interactive }) {
  return (
    <CcPanel title="Latest conversations" icon={ChatsCircle} testid="panel-recent-activity-stream" className="h-[200px]" bodyClass="!px-2" delay={0.2}>
      {recent.length ? <div className="space-y-0.5">{recent.map((c) => <RecentRow key={c.id} c={c} interactive={interactive} />)}</div>
        : <Empty>No conversations yet</Empty>}
    </CcPanel>
  );
}
